import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setCredentials } from '../../store/authSlice';
import { setUser } from '../../store/userSlice';
import { setPlayerStats } from '../../store/playerSlice';
import PasswordInput from '../Shared/PasswordInput';
import styles from './Login.module.css';

const Login = () => {
    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loginSuccess, setLoginSuccess] = useState(false);
    const [toast, setToast] = useState(null);
    const timerRef = useRef(null);
    const toastRef = useRef(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => () => { clearTimeout(timerRef.current); clearTimeout(toastRef.current); }, []);

    useEffect(() => {
        if (location.state?.passwordReset) {
            setToast('Password reset successfully. You can now log in.');
            toastRef.current = setTimeout(() => setToast(null), 4000);
        }
    }, [location.state]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/c/login/`, { identifier: identifier.toLowerCase(), password });
            const { access, refresh, username, firstname, lastname, nickname, email, player } = response.data;
            localStorage.setItem('accessToken', access);
            localStorage.setItem('refreshToken', refresh);
            localStorage.setItem('username', username);
            dispatch(setCredentials({ username, accessToken: access, refreshToken: refresh }));
            dispatch(setUser({ username, firstname, lastname, nickname, email, userID: null }));
            dispatch(setPlayerStats({
                totalGamesPlayed: player.total_games_played,
                totalGamesCompleted: player.total_games_completed,
                wins: player.wins,
                losses: player.losses,
                winningPercentage: player.winning_percentage,
                saves: player.saves,
                savesPerGame: player.saves_per_game,
                halfcups: player.half_cups,
                halfcupsPerGame: player.half_cups_per_game,
                fullCups: player.full_cups,
                fullCupsPerGame: player.full_cups_per_game,
                totalCups: player.total_cups,
                totalCupsPerGame: player.total_cups_per_game,
                ownCups: player.own_cups,
                ownCupsPerGame: player.own_cups_per_game,
                lapsGiven: player.laps_given,
                lapsReceived: player.laps_received,
                totalCupsFor: player.total_cups_for,
                totalCupsAgainst: player.total_cups_against,
                cups: player.cups,
            }));
            setLoginSuccess(true);
            timerRef.current = setTimeout(() => navigate('/main', { state: { fromAuth: true } }), 1050);
            return;
        } catch (error) {
            if (!error.response) {
                setError("Unable to connect to server. Please try again later.");
            } else if (error.response.status >= 500) {
                setError("Something went wrong. Please try again.");
            } else {
                setError("Incorrect username, email, or password.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`${styles.container} ${loginSuccess ? styles.exitFade : ''}`}>
            {toast && <div className={styles.toast}>{toast}</div>}
            <button className={styles.backButton} onClick={() => navigate('/')}>← Back</button>
            <h2 className={styles.title}>Login</h2>
            <form className={styles.form} onSubmit={handleSubmit}>
                <label className={styles.label}>
                    Username or Email
                    <input
                        className={styles.input}
                        type="text"
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        autoFocus
                        autoComplete="username"
                        required
                    />
                </label>
                <label className={styles.label}>
                    Password
                    <PasswordInput
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password"
                        required
                    />
                </label>
                {error && <p className={styles.errorMessage}>{error}</p>}
                <button
                    className={`${styles.submitButton} ${loginSuccess ? styles.submitButtonSuccess : ''}`}
                    type="submit"
                    disabled={loading || loginSuccess}
                >
                    {loading ? 'Logging in…' : loginSuccess ? 'Welcome back!' : 'Login'}
                </button>
            </form>
            <p className={styles.switchLink}>
                Don't have an account? <Link to="/createuser" className={styles.link}>Sign up</Link>
            </p>
            <p className={styles.switchLink}>
                <Link to="/forgotpassword" className={styles.link}>Forgot password?</Link>
            </p>
        </div>
    );
};

export default Login;
